/* Responsibility: Updates filter value for taxonomy checkboxes */
import fn from '../lib/fn.js';
import dom from '../lib/dom.js';
import preconditions from '../lib/preconditions.js';
import checkBoxFilterUtils from './modules/checkboxFilterUtils.js';

let lastToggled;

const checkPreconditions = preconditions('taxonomyFilter', {
    root: 'element',
    resultViewCriteria: 'object',
    eventHub: 'object',
    dataKey: 'string',
    urlKey: 'string',
    togglerSetup: 'object',
    renderedDestinations: 'string',
});

export default function setupContinentTaxonomyFilter(params) {
    checkPreconditions(params);
    const cbfn = checkBoxFilterUtils(params);
    let filterListings = [];
    const continentsTogglers = [];

    const isOnlyChild = function (children) {
        return children.length === 1;
    };

    const getElementChildrenByTagName = function (element, tagName) {
        return fn.filter((e) => e.tagName.toLowerCase() === tagName.toLowerCase(), dom.children(element));
    };

    const getElementChildrenByClass = function (element, tagName) {
        return fn.filter((e) => e.className && e.className.toLowerCase().includes(tagName.toLowerCase()), dom.children(element));
    };

    const getNextLevel = function (element) {
        return getElementChildrenByClass(dom.closestWithClass('taxonomyElement', element), 'taxonomyList')[0];
    };

    const getLevelElements = function (level) {
        return getElementChildrenByClass(level, 'taxonomyElement');
    };

    const anySiblingCheckboxesChecked = function (div) {
        return fn.some((checkDiv) => dom.byQuerySelector('input', checkDiv)[0].checked, div.parentElement.children);
    };

    const getOnlyChildInput = function (elements) {
        return dom.byQuerySelector("input[type='checkbox']", elements[0])[0];
    };

    const wasOnlyChildUnchecked = function (children) {
        return getOnlyChildInput(children).value === lastToggled;
    };

    const onlyChildIsDescendantOfLastToggled = function (children) {
        return getOnlyChildInput(children).value.indexOf(lastToggled) !== -1;
    };

    const autoCheckOnlyChildIfPresent = function (nextLevel) {
        const elements = getLevelElements(nextLevel);
        if (isOnlyChild(elements) && !wasOnlyChildUnchecked(elements) && onlyChildIsDescendantOfLastToggled(elements)) {
            getOnlyChildInput(elements).checked = true;
        }
    };

    const toggleLevelVisibility = function (element) {
        const nextLevel = getNextLevel(element);
        if (nextLevel) {
            if (cbfn.isChecked(element)) {
                dom.removeClass('u-display-none', nextLevel);
                autoCheckOnlyChildIfPresent(nextLevel);
            } else {
                dom.addClass('u-display-none', nextLevel);
                fn.forEach(nextLevel.getElementsByTagName('input'), (child) => {
                    child.checked = false;
                });
            }
        }
    };

    const leafCheckedNodes = function () {
        const allCheckedValues = cbfn.checkedValues();
        return fn.remove(
            (checkedValue) => fn.some((possibleChild) => possibleChild.indexOf(`${checkedValue};`) === 0, allCheckedValues),
            allCheckedValues,
        );
    };

    function resolveValues() {
        cbfn.eachCheckbox(toggleLevelVisibility);
        return cbfn.checkedValues();
    }

    function recursivelyUncheckParentsWhenNoSiblings(originBox) {
        const element = dom.closestWithClass('taxonomyElement', originBox);
        if (element.getAttribute('data-testid') !== 'topLevelLocationFilter' && !anySiblingCheckboxesChecked(element)) {
            const parentElement = dom.closestWithClass('taxonomyElement', element.parentElement);
            const parentInputs = dom.byQuerySelector('input', getElementChildrenByTagName(parentElement, 'label')[0]);
            if (parentInputs.length) {
                parentInputs[0].checked = false;
                recursivelyUncheckParentsWhenNoSiblings(parentInputs[0]);
            }
        }
    }

    function uncheckFromOutsideEvent(val) {
        const box = cbfn.checkbox(val);
        box.checked = false;
        params.eventHub.publish('filters:removed', { filterId: params.urlKey, itemId: val });
        recursivelyUncheckParentsWhenNoSiblings(box);
        params.resultViewCriteria[params.urlKey] = cbfn.checkedValues();
    }

    function filterRemoveEvent(val) {
        return { filterId: params.urlKey, itemId: val };
    }

    function filterAddEvent(val) {
        return {
            filterId: params.urlKey,
            itemId: val,
            urlValue: val,
            urlKey: params.urlKey,
            text: val.substring(val.lastIndexOf(';') + 1),
            remove() {
                uncheckFromOutsideEvent(val);
            },
        };
    }

    function updateFilterCheckedElm() {
        const checkedNodes = leafCheckedNodes();
        const removed = fn.map(filterRemoveEvent, fn.difference(filterListings, checkedNodes));
        const added = fn.map(filterAddEvent, fn.difference(checkedNodes, filterListings));
        if (added.length || removed.length) {
            params.eventHub.publish('filters:changed', {
                removed,
                added,
            });
            filterListings = checkedNodes;
        }
    }

    function checkboxClicked(el) {
        if (el) {
            lastToggled = el.currentTarget.defaultValue;
        }
        params.resultViewCriteria[params.urlKey] = resolveValues();
        params.eventHub.publish('resultViewCriteria:changed');
        updateFilterCheckedElm();
    }

    function countries(data) {
        const continentsWithCountries = fn.select((continent) => typeof continent.children !== 'undefined', data[params.dataKey]);

        return fn.reduce((first, second) => fn.concat(first, second.children), [], continentsWithCountries);
    }

    function setupContinentTogglers() {
        if (!continentsTogglers.length) {
            fn.forEach(dom.byQuerySelector('div[data-continent-name]', params.root), (div) => {
                const continentToggler = params.togglerSetup({
                    root: params.root,
                    rootSelector: `div[data-continent-name='${div.getAttribute('data-continent-name')}']`,
                    togglerSelector: `a[data-testid='countryToggler-${div.getAttribute('data-continent-name')}']`,
                    selector: 'countryLevel',
                    elementsToShow: 3,
                    predicate() {
                        return false;
                    },
                });
                continentsTogglers.push(continentToggler);
            });
        }
        fn.forEach(continentsTogglers, (toggler) => {
            toggler.init();
        });
    }

    function setupCountryCheckboxesWithZeroCount(data) {
        if (data[params.dataKey]) {
            fn.forEach(countries(data), (item) => {
                if (!Number.parseInt(item.count, 10)) {
                    cbfn.setState(cbfn.checkbox(item.name), 0);
                    disableCheckbox(item);
                }
            });
        }
    }

    function disableCheckbox(item) {
        const parent = dom.closestWithClass('input-toggle', cbfn.checkbox(item.name));
        dom.addClass('input-toggle--is-disabled', parent);
        const input = dom.byQuerySelector('input', parent)[0];
        input.setAttribute('disabled', '');
    }

    function toggleCheckboxVisibility() {
        cbfn.eachCheckbox(toggleLevelVisibility);
    }

    function setRenderedDestinationsHtml(data) {
        if (data && typeof data[params.renderedDestinations] !== 'undefined') {
            params.root.innerHTML = data[params.renderedDestinations];
        }
    }

    function renderElements(data) {
        setRenderedDestinationsHtml(data);
        setupContinentTogglers();
        setupCountryCheckboxesWithZeroCount(data);
        toggleCheckboxVisibility();
        updateFilterCheckedElm();
    }

    function uncheckValuesRemovedFromUrl(query) {
        const removeEvents = [];
        cbfn.eachCheckedCheckbox((box) => {
            if (!query || fn.indexOf(box.value, query) === -1) {
                box.checked = false;
                removeEvents.push(filterRemoveEvent(box.value));
            }
        });
        return removeEvents;
    }

    function checkValuesInUrl(queries) {
        const allCheckedBoxValues = [];
        if (queries) {
            fn.forEach(queries, (query) => {
                let accumulated = '';
                fn.forEach(query.split(';'), (levelValue) => {
                    accumulated += (accumulated ? ';' : '') + levelValue;
                    const box = cbfn.checkbox(accumulated);
                    if (box) {
                        allCheckedBoxValues.push(filterAddEvent(accumulated));
                        box.checked = true;
                    }
                });
            });
        }
        return allCheckedBoxValues;
    }

    function setStateFromUrl() {
        if (!params.urlQueryParams) {
            return;
        }
        const uncheckedValues = uncheckValuesRemovedFromUrl(params.urlQueryParams[params.urlKey]);
        const checkedValues = checkValuesInUrl(params.urlQueryParams[params.urlKey]);
        if (uncheckedValues.length || checkedValues.length) {
            params.resultViewCriteria[params.urlKey] = resolveValues();
            updateFilterCheckedElm();
        }
    }

    params.eventHub.subscribe('polling-complete', setStateFromUrl);
    params.eventHub.subscribe('urlQueryParams:changed', setStateFromUrl);
    params.eventHub.subscribe('changed-results', renderElements);
    dom.on(params.root, 'click', 'input', checkboxClicked);
}
